// Billing constants for Fundsorter
// Centralised location for billing-related configuration values

// New Zealand GST rate (15%)
export const GST_RATE = 0.15
export const ANNUAL_DISCOUNT_RATE = 0.2

/**
 * Calculate GST amount for a given ex-GST amount
 */
export function calculateGst(amount: number): number {
  return Math.round(amount * GST_RATE * 100) / 100
}

/**
 * Add GST to an ex-GST amount, returning the inc-GST total
 */
export function addGst(amount: number): number {
  return Math.round(amount * (1 + GST_RATE) * 100) / 100
}

/**
 * Remove GST from an inc-GST amount, returning the ex-GST amount
 */
export function removeGst(amount: number): number {
  return Math.round((amount / (1 + GST_RATE)) * 100) / 100
}

export interface TierPricingInfo {
  monthly: number
  description: string
  turnover: string
}

export type BillingFrequency = 'monthly' | 'quarterly' | 'annual'

export const TIER_PRICING: Record<number, TierPricingInfo> = {
  0: { monthly: 20, description: 'Tier Micro', turnover: 'Under $40k' },
  1: {
    monthly: 125,
    description: 'Tier 1 - Large Organisation',
    turnover: 'Over $33m',
  },
  2: {
    monthly: 125,
    description: 'Tier 2 - Medium Organisation',
    turnover: '$5m - $33m',
  },
  3: {
    monthly: 50,
    description: 'Tier 3 - Small Organisation',
    turnover: '$140k - $5m',
  },
  4: {
    monthly: 35,
    description: 'Tier 4 - Mini Organisation',
    turnover: 'Under $140k',
  },
}

export function getBillingAmount(
  monthlyPrice: number,
  billingFrequency: BillingFrequency
): number {
  if (billingFrequency === 'annual') {
    return Math.round(monthlyPrice * 12 * (1 - ANNUAL_DISCOUNT_RATE) * 100) / 100
  }

  const frequencyMultiplier: Record<'monthly' | 'quarterly', number> = {
    monthly: 1,
    quarterly: 3,
  }

  return monthlyPrice * frequencyMultiplier[billingFrequency]
}

/**
 * Get display label for a tier number
 */
export function getTierLabel(tier: number | null | undefined): string {
  if (tier === null || tier === undefined) return 'Tier 4'
  if (tier < 0) return 'Custom'
  if (tier === 0) return 'Tier Micro'
  return `Tier ${tier}`
}
